import { FC, ReactNode } from 'react';
import cx from 'classnames';
import Accordion from '@/storybook/components/Accordion/Accordion';
import Badge from '@/storybook/components/Badge/Badge';
import FilterChip from '@/storybook/components/FilterChip/FilterChip';
import Text from '@/storybook/components/Text/Text';
import styles from './FilterSetPreview.module.css';

export interface FilterChipItem {
  key: string;
  label: string;
  content?: ReactNode;
}

export interface ChannelGroup {
  channelKey: string;
  channelLabel: string;
  chips: FilterChipItem[];
}

export interface LocalFilterSection {
  sectionKey: string;
  sectionLabel: string;
  channelGroups?: ChannelGroup[];
  chips?: FilterChipItem[];
}

export interface FilterSetPreviewProps {
  'title'?: string;
  'dateRangeText'?: string;
  'dateRangeLabel'?: string;
  'globalFilterLabel'?: string;
  'globalFilterChips'?: FilterChipItem[];
  'localFilterSections'?: LocalFilterSection[];
  /** Key of the active local-filter section. `null` = no section active (all inactive). `undefined` = no distinction. */
  'activeSectionKey'?: string | null;
  'activeChannelKey'?: string;
  'inactiveLabel'?: string;
  /** When true, all sections and channels are shown with equal styling (no inactive distinction). */
  'showAllActive'?: boolean;
  'className'?: string;
  'data-testid'?: string;
  'renderChip'?: (chip: FilterChipItem) => ReactNode;
}

const PreviewChip: FC<{ chip: FilterChipItem; inactive?: boolean }> = ({ chip, inactive }) => (
  <FilterChip
    label={chip.label}
    content={chip.content}
    size="S"
    isDisabled={inactive}
    className={styles.chip}
    data-testid={`preview-chip-${chip.key}`}
  />
);

const InactiveBadge: FC<{ label: string }> = ({ label }) => (
  <Badge variant="light" color="grey" size="S">
    {label}
  </Badge>
);

const FilterSetPreview: FC<FilterSetPreviewProps> = ({
  title,
  dateRangeText,
  dateRangeLabel,
  globalFilterLabel,
  globalFilterChips,
  localFilterSections,
  activeSectionKey,
  activeChannelKey,
  inactiveLabel,
  showAllActive = false,
  className,
  'data-testid': dataTestId = 'filter-set-preview',
  renderChip,
}) => {
  const renderChipItem = (chip: FilterChipItem, inactive?: boolean) =>
    renderChip ? renderChip(chip) : <PreviewChip key={chip.key} chip={chip} inactive={inactive} />;

  return (
    <div className={className} data-testid={dataTestId}>
      {title && <Text className={styles.title}>{title}</Text>}

      {dateRangeText != null && (
        <div className={styles.filterRow} data-testid={`${dataTestId}-date-range`}>
          <Text as="span" className={styles.filterLabel}>{dateRangeLabel}</Text>
          <div className={styles.filterChips}>
            <FilterChip label={dateRangeText} size="S" className={styles.chip} data-testid="preview-chip-date-range" />
          </div>
        </div>
      )}

      {globalFilterChips != null && globalFilterChips.length > 0 && (
        <div className={styles.filterRow} data-testid={`${dataTestId}-global`}>
          <Text as="span" className={styles.filterLabel}>{globalFilterLabel}</Text>
          <div className={styles.filterChips}>{globalFilterChips.map(chip => renderChipItem(chip))}</div>
        </div>
      )}

      {localFilterSections?.map(section => {
        const hasContent =
          (section.chips && section.chips.length > 0) ||
          (section.channelGroups && section.channelGroups.some(group => group.chips.length > 0));
        if (!hasContent) return null;

        const isInactive = !showAllActive && activeSectionKey !== undefined && section.sectionKey !== activeSectionKey;

        const sectionTitle = (
          <Text as="span" className={cx(styles.localSectionTitle, isInactive && styles.localSectionTitleInactive)}>
            {section.sectionLabel}
            {isInactive && inactiveLabel && <InactiveBadge label={inactiveLabel} />}
          </Text>
        );

        return (
          <Accordion
            key={section.sectionKey}
            title={sectionTitle}
            size="S"
            defaultExpanded
            disableBodyHoverBorder
            className={styles.localSection}
            contentClassName={styles.localSectionContent}
            data-testid={`${dataTestId}-local-${section.sectionKey}`}
          >
            {section.channelGroups?.map(group => {
              if (group.chips.length === 0) return null;
              const isChannelInactive =
                !showAllActive &&
                (isInactive || (!isInactive && activeChannelKey != null && group.channelKey !== activeChannelKey));
              return (
                <div key={group.channelKey} className={styles.channelGroup}>
                  <Text as="span" className={cx(styles.channelLabel, isChannelInactive && styles.channelLabelInactive)}>
                    {group.channelLabel}:
                  </Text>
                  {group.chips.map(chip => renderChipItem(chip, isChannelInactive))}
                </div>
              );
            })}

            {section.chips && section.chips.length > 0 && (
              <div className={styles.filterChips}>{section.chips.map(chip => renderChipItem(chip, isInactive))}</div>
            )}
          </Accordion>
        );
      })}
    </div>
  );
};

export default FilterSetPreview;
